import useTranslation from 'hooks/useTranslation/useTranslation';
import { useState } from 'react';
import useModalContext from 'v2/components/Modal/useModalContext';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import WizardModalTemplate, { WizardStep } from 'v2/components/WizardModal/WizardModalTemplate';
import useExternalUsers from 'v2/hooks/useExternalUsers/useExternalUsers';
import GroupDevicesStep from './GroupDevicesStep';
import GroupNameStep, { ScheduleGrant } from './GroupNameStep';
import GroupScheduleStep from './GroupScheduleStep';
import GroupSettingsStep from './GroupSettingsStep';

interface WizardData {
  groupId: string;
  groupName: string;
  scheduleGrant: ScheduleGrant | null;
}

const AddUserGroupModal = () => {
  const { translation } = useTranslation();
  const { closeModal } = useModalContext();
  const { showSnackbar } = useSnackbarContext();
  const { externalUsersGroups } = useExternalUsers({
    translation,
    handleFetchError: (msg) => showSnackbar(msg, { severity: 'error' })
  });

  const [wizardData, setWizardData] = useState<WizardData | null>(null);

  const steps: WizardStep[] = [
    { label: translation.group_name },
    { label: translation.settings, description: translation.optional },
    { label: translation.schedule, description: translation.optional },
    { label: translation.device_groups, description: translation.optional }
  ];

  const handleGroupCreated = (groupId: string, groupName: string, scheduleGrant: ScheduleGrant | null) => {
    setWizardData({ groupId, groupName, scheduleGrant });
  };

  const handleFinish = () => {
    showSnackbar(translation.group_created, { severity: 'success' });
    closeModal();
  };

  return (
    <WizardModalTemplate id="add-user-group-modal" title={translation.new_group} steps={steps} onFinish={handleFinish}>
      <GroupNameStep
        existingGroupNames={externalUsersGroups.map((group) => group.name)}
        onGroupCreated={handleGroupCreated}
      />
      <GroupSettingsStep groupId={wizardData?.groupId ?? null} groupName={wizardData?.groupName ?? null} />
      <GroupScheduleStep
        grantId={wizardData?.scheduleGrant?.id ?? null}
        weekDays={wizardData?.scheduleGrant?.weekDays ?? []}
      />
      <GroupDevicesStep groupId={wizardData?.groupId ?? null} accessGrantId={wizardData?.scheduleGrant?.id ?? null} />
    </WizardModalTemplate>
  );
};

export default AddUserGroupModal;
